import { useMutation, useQuery } from "@tanstack/react-query";

import {
  getTitleIssue,
  getTitleIssues,
  getTitleIssueStatusCounts,
  forceTitleIssueCheck,
  saveWiLienResult,
} from "@Http/titile_issues";
import { Deal } from "@Types/deal";
import { HttpQuery, PaginatedResult } from "@Types/http";
import { MutationError } from "@Types/index";
import {
  TitleIssue,
  LienInfo,
  TitleIssueStatusCounts,
} from "@Types/title_issues";

import { queryClient } from "../App";

const useGetTitleIssues = (query?: HttpQuery) => {
  const queryKey = ["title_issues", query ?? []];

  const { data, isLoading, error } = useQuery<
    PaginatedResult<TitleIssue[]>,
    MutationError,
    PaginatedResult<TitleIssue[]>
  >({
    queryKey: queryKey,
    queryFn: getTitleIssues(query),
  });

  return {
    data: data?.docs ?? [],
    totalPages: data?.totalPages ?? 0,
    totalDocs: data?.totalDocs ?? 0,
    isLoading,
    error,
  };
};
export const useGetTitleIssueStatusCounts = () => {
  const { data, isLoading, error } = useQuery<
    TitleIssueStatusCounts,
    MutationError,
    TitleIssueStatusCounts
  >({
    queryKey: ["title_issue_status_counts"],
    queryFn: getTitleIssueStatusCounts(),
  });

  return {
    data,
    isLoading,
    error,
  };
};

export const useGetTitleIssue = (id?: string) => {
  const queryKey = [`title-issue-${id}`];

  const {
    data: titleIssue,
    isLoading,
    error,
  } = useQuery<TitleIssue, MutationError, TitleIssue>({
    queryKey: queryKey,
    queryFn: getTitleIssue(id as string),
    enabled: !!id,
  });

  return {
    titleIssue,
    isLoading,
    error,
  };
};

export const useForceTitleIssueCheck = (deal: Deal) => {
  const mutation = useMutation<
    TitleIssue,
    MutationError,
    TitleIssue & { state: string }
  >(forceTitleIssueCheck(), {
    onSuccess: (title) => {
      queryClient.setQueryData<Deal>(["deal", deal._id], {
        ...deal,
        data: { ...deal.data, titleIssue: title },
      });
    },
  });
  return {
    forceTitleIssueCheck: mutation.mutate,
    loading: mutation.isLoading,
    success: mutation.isSuccess,
    error: mutation.error,
  };
};

export const useWisconsinLienCheck = (titleIssueId: string) => {
  const mutation = useMutation<
    LienInfo,
    MutationError,
    {
      titleIssueId: string;
      hasLien: boolean;
      lienholderName?: string | null;
      lienholderAddress?: string | null;
      receivedDate?: string | null;
    }
  >(saveWiLienResult(), {
    onSuccess: () => {
      queryClient.invalidateQueries([`title-issue-${titleIssueId}`]);
    },
  });

  return {
    saveResult: mutation.mutate,
    saving: mutation.isLoading,
    saved: mutation.isSuccess,
    saveError: mutation.error,
    reset: mutation.reset,
  };
};

export default useGetTitleIssues;
